import React, { useState, useEffect } from "react";
import { Package } from "lucide-react";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import "./AddProductsModal.scss";
import { toast } from "react-toastify";

// Adding new products no longer happens in this modal — it now redirects to
// the full <EnquiryCreateForm />, pre-filled with this enquiry's existing
// data, via onEditEnquiry(enquiryId). That form already has its own product
// catalog + cart, so we don't need a second copy of that UI here. This modal
// is left doing exactly one thing: updating the fulfillment status of
// products that are already on the enquiry.
const AddProductsModal = ({
  isOpen,
  onClose,
  onStatusUpdate,
  onEditEnquiry,
  currentProducts = [],
  enquiryId,
}) => {
  const [existingProducts, setExistingProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Helper function to normalize status to uppercase for comparison
  const normalizeStatus = (status) => {
    if (!status) return "PENDING";
    return status.toUpperCase();
  };

  // Helper function to format status for display (capitalize first letter)
  const formatStatusForDisplay = (status) => {
    if (!status) return "Pending";
    const normalized = normalizeStatus(status);
    switch (normalized) {
      case "PENDING":
        return "Pending";
      case "PARTIAL":
        return "Partial";
      case "FULFILLED":
        return "Fulfilled";
      case "CANCELLED":
        return "Cancelled";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setExistingProducts([]);
    } else {
      // Set existing products with fulfillmentStatus from API
      const formattedExisting = currentProducts.map((product) => {
        // Get the fulfillmentStatus from the product object
        const apiFulfillmentStatus = product.fulfillmentStatus || "PENDING";
        const normalizedStatus = normalizeStatus(apiFulfillmentStatus);

        return {
          ...product,
          fulfillmentStatus: normalizedStatus, // Use normalized status for comparison
          originalStatus: normalizedStatus, // Store original for comparison
          displayStatus: apiFulfillmentStatus, // Keep original for display
        };
      });
      setExistingProducts(formattedExisting);
    }
  }, [isOpen, currentProducts]);

  const updateExistingProductStatus = (index, status) => {
    setExistingProducts((prev) =>
      prev.map((product, i) =>
        i === index ? { ...product, fulfillmentStatus: status } : product,
      ),
    );
  };

  const handleSave = async () => {
    const hasStatusChanges = existingProducts.some(
      (product) => product.fulfillmentStatus !== product.originalStatus,
    );

    if (!hasStatusChanges) {
      toast.warning("No changes to save");
      return;
    }

    setLoading(true);

    try {
      if (!onStatusUpdate) {
        console.error("onStatusUpdate handler is missing!");
        toast.error("Status update handler not configured");
        setLoading(false);
        return;
      }

      // The only thing editable on an existing product here is its
      // fulfillment status — pricing/discount/quantity and adding new
      // products live in the full "Edit Enquiry" form instead.
      const changedProducts = existingProducts
        .filter(
          (product) => product.fulfillmentStatus !== product.originalStatus,
        )
        .map((product) => ({
          id: product.id,
          fulfillmentStatus: product.fulfillmentStatus, // Send uppercase to API
          status: product.fulfillmentStatus,
          statusChanged: true,
        }));

      const success = await onStatusUpdate(changedProducts);
      if (success) {
        onClose();
      }
    } catch (error) {
      console.error("Error saving products:", error);
      toast.error("Failed to save changes");
    } finally {
      setLoading(false);
    }
  };

  const getFulfillmentStatusColor = (status) => {
    const normalizedStatus = normalizeStatus(status);
    switch (normalizedStatus) {
      case "FULFILLED":
        return "#38A169";
      case "PARTIAL":
        return "#D69E2E";
      case "CANCELLED":
        return "#E53E3E";
      default:
        return "#718096";
    }
  };

  const getFulfillmentStatusIcon = (status) => {
    const normalizedStatus = normalizeStatus(status);
    switch (normalizedStatus) {
      case "FULFILLED":
        return "✓";
      case "PARTIAL":
        return "⧗";
      case "CANCELLED":
        return "✕";
      default:
        return "○";
    }
  };

  const getStatusButtonStyle = (product, status) => {
    // Check if this is the current status for this product
    const isActive = product.fulfillmentStatus === status;
    const color = getFulfillmentStatusColor(status);

    return {
      borderColor: isActive ? color : "#CBD5E0",
      backgroundColor: isActive ? color : "transparent",
      color: isActive ? "white" : "#4A5568",
    };
  };

  // Status options in uppercase for consistent comparison
  const STATUS_OPTIONS = ["PENDING", "PARTIAL", "FULFILLED", "CANCELLED"];

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="add-products-modal enhanced"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="header-content">
            <Package size={24} />
            <div>
              <h2>Manage Enquiry Products</h2>
              <p className="header-subtitle">
                Update fulfillment status, or add products via the enquiry form
              </p>
            </div>
          </div>
          <div className="header-actions">
            {onEditEnquiry && (
              <ButtonWithIcon
                icon="mdi:cart-plus"
                text="Edit Products"
                onClick={() => onEditEnquiry(enquiryId)}
                className="edit-button"
                type="button"
                title="Add products via the enquiry form"
              />
            )}
          </div>
        </div>

        <div className="modal-body">
          <div className="existing-products-section">
            <div className="section-header">
              <h3>Update Product Fulfillment Status</h3>
            </div>

            {existingProducts.length > 0 ? (
              <div className="existing-products-list">
                {existingProducts.map((product, index) => (
                  <div key={index} className="existing-product-item">
                    <div className="product-info">
                      <div className="product-header">
                        <span className="product-name">
                          {product.productName || `Product #${product.id}`}
                        </span>
                        <span className="product-qty">
                          Qty: {product.quantity}
                        </span>
                      </div>
                      {product.skuCode && (
                        <span className="sku-code">SKU: {product.skuCode}</span>
                      )}
                      {product.price > 0 && (
                        <span className="product-price">
                          ₹{(product.price * product.quantity).toFixed(2)}
                        </span>
                      )}
                    </div>

                    <div className="status-control">
                      <label>Select New Fulfillment Status:</label>
                      <div className="status-buttons">
                        {STATUS_OPTIONS.map((status) => (
                          <button
                            key={status}
                            type="button"
                            className={`status-btn ${
                              product.fulfillmentStatus === status
                                ? "active"
                                : ""
                            }`}
                            style={getStatusButtonStyle(product, status)}
                            onClick={() =>
                              updateExistingProductStatus(index, status)
                            }
                          >
                            {getFulfillmentStatusIcon(status)}{" "}
                            {formatStatusForDisplay(status)}
                          </button>
                        ))}
                      </div>
                      {product.fulfillmentStatus !== product.originalStatus && (
                        <span className="status-changed-indicator">
                          <span className="indicator-dot">●</span>
                          Status changed from "
                          <strong>
                            {formatStatusForDisplay(product.originalStatus)}
                          </strong>
                          " to "
                          <strong>
                            {formatStatusForDisplay(product.fulfillmentStatus)}
                          </strong>
                          " - will be updated on save
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-products">
                <Package size={48} />
                <p>No existing products in this enquiry</p>
                {onEditEnquiry && (
                  <small>
                    Use "Add Products" above to add products via the enquiry
                    form
                  </small>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <ButtonWithIcon
            icon="mdi:cancel"
            text="Cancel"
            onClick={onClose}
            className="cancel-button"
            type="button"
          />
          <ButtonWithIcon
            icon={loading ? "mdi:loading" : "mdi:check"}
            text={loading ? "Saving..." : "Update Status"}
            onClick={handleSave}
            className="submit-button"
            type="button"
            disabled={
              loading ||
              !existingProducts.some(
                (p) => p.fulfillmentStatus !== p.originalStatus,
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default AddProductsModal;
